import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, X, Check, Minus, Share2, FileDown, Loader2 } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import QrShareDialog from "@/components/QrShareDialog";

const fmtPrice = (v) => v && v > 0
  ? new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v)
  : "Prix sur demande";
const fmtKm = (v) => new Intl.NumberFormat("fr-BE").format(v || 0) + " km";

export default function Compare() {
  const { ids, remove, clear, setAll } = useCompare();
  const { pick } = useLang();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const nav = useNavigate();
  const tableRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate context from ?ids=a,b,c on first load
  useEffect(() => {
    const raw = searchParams.get("ids");
    if (!raw) return;
    const arr = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (arr.length > 0) {
      setAll(arr);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ids.length === 0) { setVehicles([]); setLoading(false); return; }
    setLoading(true);
    Promise.all(ids.map((id) => api.get(`/vehicles/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => setVehicles(arr.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [ids]);

  if (ids.length === 0) {
    return (
      <div className="pt-32 pb-24 bg-[#0D0D0D] min-h-screen">
        <SEO title="Comparateur de véhicules" description="Comparez côte à côte plusieurs Porsche et véhicules de prestige de notre catalogue." />
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">03 — Comparateur</p>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05] font-light text-white mb-6">Aucun véhicule à comparer</h1>
          <p className="text-white/60 mb-10 max-w-xl mx-auto">Sélectionnez jusqu'à 4 véhicules depuis le stock en cliquant sur l'icône « comparer » sur chaque fiche.</p>
          <Link to="/stock" data-testid="compare-cta-stock" className="inline-flex items-center gap-2 bg-[#C8A75A] text-black hover:bg-white px-8 py-4 rounded-md text-sm uppercase tracking-widest font-medium">
            <ArrowLeft className="w-4 h-4" /> Retour au stock
          </Link>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-[#0D0D0D] pt-32 text-center text-white/40">Chargement…</div>;

  // Highlight best/worst per row (numeric fields only)
  const bestOf = (key, direction = "asc") => {
    const vals = vehicles.map((v) => Number(v[key]) || null).filter((x) => x !== null && x > 0);
    if (!vals.length) return null;
    return direction === "asc" ? Math.min(...vals) : Math.max(...vals);
  };
  const bests = {
    price: bestOf("price", "asc"),          // cheaper = better
    year: bestOf("year", "desc"),           // newer = better
    mileage: bestOf("mileage", "asc"),      // fewer km = better
    power_hp: bestOf("power_hp", "desc"),   // more hp = better
  };

  const rows = [
    { key: "photo",        label: "" },
    { key: "brand",        label: "Marque" },
    { key: "model",        label: "Modèle" },
    { key: "price",        label: "Prix" },
    { key: "year",         label: "Année" },
    { key: "mileage",      label: "Kilométrage" },
    { key: "power_hp",     label: "Puissance" },
    { key: "fuel_type",    label: "Carburant" },
    { key: "transmission", label: "Transmission" },
    { key: "body_type",    label: "Carrosserie" },
    { key: "color",        label: "Couleur" },
    { key: "warranty_months", label: "Garantie" },
    { key: "financing_available", label: "Financement" },
    { key: "delivery_europe", label: "Livraison Europe" },
    { key: "category",     label: "Rubrique" },
    { key: "status",       label: "Statut" },
  ];

  const render = (v, key) => {
    switch (key) {
      case "photo":
        return (
          <div className="relative aspect-[4/3] rounded-md overflow-hidden bg-[#0D0D0D]">
            <img src={v.images?.[0] || "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4807217354.jpg"} alt={v.model} className="w-full h-full object-cover" loading="lazy" />
          </div>
        );
      case "price":            return fmtPrice(v.price);
      case "mileage":          return fmtKm(v.mileage);
      case "power_hp":         return v.power_hp > 0 ? `${v.power_hp} ch` : "—";
      case "warranty_months":  return v.warranty_months > 0 ? `${v.warranty_months} mois` : "—";
      case "financing_available":
      case "delivery_europe":  return v[key] ? <Check className="w-4 h-4 text-emerald-400" /> : <Minus className="w-4 h-4 text-white/30" />;
      default:                 return v[key] || "—";
    }
  };

  const isBest = (v, key) => {
    if (!(key in bests) || bests[key] === null) return false;
    const val = Number(v[key]) || null;
    return val === bests[key] && vehicles.length > 1;
  };

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/compare?ids=${vehicles.map((v) => v.id).join(",")}`
    : "";
  const waMessage = `Comparaison ${vehicles.length} véhicules KV International :\n${vehicles.map((v) => `· ${v.brand} ${v.model} (${v.year}) — ${fmtPrice(v.price)}`).join("\n")}`;

  const exportPdf = async () => {
    if (!tableRef.current) return;
    setPdfBusy(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const el = tableRef.current;
      const canvas = await html2canvas(el, { backgroundColor: "#0D0D0D", scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL("image/jpeg", 0.9);
      // Landscape A4
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // Header
      pdf.setFillColor(13, 13, 13); pdf.rect(0, 0, pageW, pageH, "F");
      pdf.setTextColor(200, 167, 90); pdf.setFontSize(10);
      pdf.text("KV INTERNATIONAL · COMPARAISON VÉHICULES", 14, 12);
      pdf.setTextColor(255, 255, 255); pdf.setFontSize(8);
      pdf.text(new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "long", year: "numeric" }), pageW - 14, 12, { align: "right" });
      // Image content
      const ratio = canvas.width / canvas.height;
      const availW = pageW - 20;
      const availH = pageH - 28;
      let w = availW, h = availW / ratio;
      if (h > availH) { h = availH; w = availH * ratio; }
      pdf.addImage(imgData, "JPEG", (pageW - w) / 2, 20, w, h);
      pdf.setTextColor(150, 150, 150); pdf.setFontSize(7);
      pdf.text("Chaussée de Waterloo 242 · 1640 Rhode-Saint-Genèse · contact@kvinternational.be", pageW / 2, pageH - 6, { align: "center" });
      pdf.save(`kv-comparaison-${vehicles.length}vh-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur génération PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const cols = vehicles.length;
  const gridTemplate = `220px repeat(${cols}, minmax(0, 1fr))`;

  return (
    <div data-testid="compare-page" className="pt-32 pb-24 bg-[#0D0D0D] min-h-screen">
      <SEO title={`Comparer ${cols} véhicules · KV International`} description="Comparaison côte à côte de véhicules de prestige." />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">03 — Comparateur</p>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] font-light text-white">Comparaison · {cols} véhicule{cols > 1 ? "s" : ""}</h1>
            <p className="text-white/60 mt-3">Les meilleures valeurs numériques sont surlignées en or.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="compare-share" onClick={() => setShareOpen(true)} className="text-xs uppercase tracking-widest text-[#25D366] hover:text-white border border-[#25D366]/40 hover:border-[#25D366] px-4 py-3 rounded-md flex items-center gap-2">
              <Share2 className="w-3.5 h-3.5" /> Partager
            </button>
            <button data-testid="compare-pdf" onClick={exportPdf} disabled={pdfBusy} className="text-xs uppercase tracking-widest text-black bg-[#C8A75A] hover:bg-white disabled:opacity-50 px-4 py-3 rounded-md flex items-center gap-2 font-medium">
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
              {pdfBusy ? "Génération…" : "Télécharger PDF"}
            </button>
            <button data-testid="compare-clear-all" onClick={() => { clear(); nav("/stock"); }} className="text-xs uppercase tracking-widest text-white/50 hover:text-red-400 border border-white/15 hover:border-red-400/40 px-4 py-3 rounded-md">Tout effacer</button>
            <Link to="/stock" className="text-xs uppercase tracking-widest text-[#C8A75A] hover:text-white border border-[#C8A75A]/40 px-4 py-3 rounded-md">Ajouter →</Link>
          </div>
        </div>

        {/* Table */}
        <div ref={tableRef} className="overflow-x-auto">
          <div style={{ display: "grid", gridTemplateColumns: gridTemplate, minWidth: cols * 260 + 220 }} className="gap-3">
            {/* Header cells: vehicle name + remove */}
            <div />
            {vehicles.map((v) => (
              <div key={v.id} className="bg-[#1E1E1E] border border-white/10 rounded-md p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{v.brand} · {v.year}</p>
                    <Link to={`/stock/${v.id}`} className="font-display text-lg text-white hover:text-[#C8A75A] transition-colors line-clamp-2">{v.model}</Link>
                  </div>
                  <button data-testid={`compare-remove-${v.id}`} onClick={() => remove(v.id)} title="Retirer" className="text-white/40 hover:text-red-400 p-1"><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}

            {/* Rows */}
            {rows.map((r) => (
              <RowGroup key={r.key} label={r.label} cols={cols}>
                {vehicles.map((v) => (
                  <div key={v.id} className={`bg-[#1E1E1E] border rounded-md p-4 text-sm ${isBest(v, r.key) ? "border-[#C8A75A] bg-[#C8A75A]/5" : "border-white/10"}`}>
                    <div className={`text-white ${r.key === "photo" ? "-m-4" : ""} ${isBest(v, r.key) ? "text-[#C8A75A] font-semibold" : ""}`}>
                      {render(v, r.key)}
                    </div>
                  </div>
                ))}
              </RowGroup>
            ))}

            {/* Description */}
            <div className="flex items-start pt-4 text-[10px] uppercase tracking-[0.25em] text-white/40">Description</div>
            {vehicles.map((v) => (
              <div key={v.id} className="bg-[#1E1E1E] border border-white/10 rounded-md p-4 text-sm text-white/70 leading-relaxed">
                <p className="line-clamp-6">{pick(v.description) || "—"}</p>
              </div>
            ))}

            {/* CTA row */}
            <div />
            {vehicles.map((v) => (
              <div key={v.id} className="pt-2">
                <Link to={`/stock/${v.id}`} data-testid={`compare-detail-${v.id}`} className="block text-center bg-[#C8A75A] text-black hover:bg-white transition-colors px-4 py-3 rounded-md text-xs uppercase tracking-widest font-medium">
                  Fiche complète
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      <QrShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`Comparaison · ${vehicles.length} véhicules`}
        subtitle={vehicles.map((v) => `${v.brand} ${v.model}`).join(" · ")}
        url={shareUrl}
        waMessage={waMessage}
        downloadName={`kv-comparaison-${vehicles.length}vh`}
      />
    </div>
  );
}

function RowGroup({ label, cols, children }) {
  return (
    <>
      <div className="flex items-center py-2 text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
      {children}
    </>
  );
}

// helpers attached via closure inside Compare — declared after component to keep file tidy
